import config from 'config/common';
import { ThemeOptions } from 'interfaces/main';
import { LocalStorageKeys } from 'constants/localStorage.constants';

export const getThemeFromLocalStorage: () => any = () =>
    localStorage.getItem(config.theme.local_storage_key) || config.theme.default_theme;

export const setThemeToLocalStorage = (theme: ThemeOptions) =>
    localStorage.setItem(config.theme.local_storage_key, theme);

export const getRenewPlatformUser = () => {
    return Boolean(sessionStorage.getItem(config.platformRenewUserKey));
};

export const setRenewPlatformUserToSS = () => {
    return sessionStorage.setItem(config.platformRenewUserKey, 'true');
};

export const removeRenewPlatformUserToSS = () => {
    return sessionStorage.removeItem(config.platformRenewUserKey);
};

export function parseMarketingInfoFromStorage(): object {
    try {
        const raw = localStorage.getItem(LocalStorageKeys.MARKETING_INFO);
        return raw ? JSON.parse(raw) : {};
    } catch {
        return {};
    }
}

/**
 * Dev-only override for the Guest iframe base URL (RND-1014).
 * Lets developers point the iframe at a different environment via
 * `localStorage[GUEST_IFRAME_URL_OVERRIDE]` without a config change or rebuild.
 *
 * The override is honored only in non-production builds, because the backend URL
 * carries a JWT in its query string — sending it to an arbitrary destination in
 * production would leak the token (URLs land in logs / Referer headers).
 * Query params the backend embedded (the JWT, etc.) are merged onto the override
 * so the iframe stays authenticated. Absent / empty / invalid override falls back
 * to the backend URL (current behavior).
 *
 * Provide a concrete URL — the `{lng}` language placeholder is not applied to
 * overrides (it gets percent-encoded when the override is normalized).
 */
export const resolveGuestIframeBaseUrl = (backendUrl: string): string => {
    const isProduction = ['production', 'prod'].includes(import.meta.env.REACT_APP_ENV);
    if (isProduction) return backendUrl;

    const override = localStorage.getItem(LocalStorageKeys.GUEST_IFRAME_URL_OVERRIDE);
    if (!override) return backendUrl;

    let overrideUrl: URL;
    try {
        overrideUrl = new URL(override);
    } catch {
        return backendUrl;
    }
    if (overrideUrl.protocol !== 'http:' && overrideUrl.protocol !== 'https:') {
        return backendUrl;
    }

    try {
        const backend = new URL(backendUrl);
        backend.searchParams.forEach((value, key) => {
            if (!overrideUrl.searchParams.has(key)) {
                overrideUrl.searchParams.set(key, value);
            }
        });
    } catch {
        // backendUrl not parseable — use the override on its own
    }

    return overrideUrl.toString();
};
